import React from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { KRAJ } from '../../kraj'
import { formatPrice } from '../../utils'
import StronaDokumentu from './StronaDokumentu'

/**
 * Okładka dokumentu „VoltValue Projekt".
 *
 * PO CO: dokument bywa załącznikiem do oferty dla klienta końcowego. Bez okładki
 * zaczynał się od schematu łańcuchów — rysunku technicznego, który klientowi nic
 * nie mówi i wygląda jak wydruk z programu, a nie jak oferta.
 *
 * NAZWA FIRMY POCHODZI Z KONTA (rejestr / dane firmy), nie z pola do wpisania:
 * to ta sama nazwa, którą klient zobaczy na fakturze. Gdy konto nie ma jeszcze
 * podpiętej firmy, nagłówek po prostu jej nie pokazuje — pusta ramka albo napis
 * „Twoja firma" na dokumencie wysłanym klientowi byłby gorszy niż jego brak.
 *
 * ŻADNYCH RAMEK NA MIEJSCA, KTÓRE MOGĄ ZOSTAĆ PUSTE. Kreskowana ramka wokół
 * logo wygląda dobrze w makiecie, ale przy mniejszym logo klient dostaje na
 * ofercie widoczne obramowanie pustego pola. Miejsce na logo jest po prostu
 * białe i tyle.
 */

/**
 * Grafika okładki dobierana do TEJ instalacji, a nie jedna na wszystko.
 *
 * Dwie osie: gdzie stoją moduły (dach czy grunt) i czy jest magazyn energii.
 * Klient ogląda okładkę dłużej niż resztę dokumentu i rysunek pokazujący dach,
 * gdy instalacja idzie na grunt, podważa wiarygodność wszystkiego dalej —
 * zanim jeszcze dojdzie do liczb.
 *
 * „Grunt" to JEDEN klucz konstrukcji (`grunt`, patrz lib/konstrukcja); każdy
 * inny rodzaj połaci to dach. Dlatego warunek jest na równość z `grunt`,
 * a nie lista dachów — nowy rodzaj pokrycia dachowego ma trafić do wariantu
 * dachowego sam, bez dopisywania go tutaj.
 */
const grafikaDla = ({ naGruncie, zMagazynem }) => {
  const podloze = naGruncie ? 'grunt' : 'roof'
  const wariant = zMagazynem ? 'magazyn' : 'sieciowy'
  return `${process.env.PUBLIC_URL}/projekt/${podloze}-${wariant}.png`
}

// Klucze obietnic w słowniku `projekt` (tytulowa.obietnice.*).
const OBIETNICE = ['kompatybilnosc', 'obliczenia', 'gwarancje']

/**
 * Kafelek z jedną liczbą. Bez wartości nie renderuje się wcale.
 *
 * Celowo NISKI: cztery kafelki stoją w rzędzie, a każdy zaoszczędzony milimetr
 * ich wysokości decyduje o tym, czy spis treści zmieści się na pierwszej
 * kartce. Kreska w kolorze firmy zeszła do roli cienkiego akcentu nad etykietą,
 * bo w wersji z odstępem zabierała tyle miejsca co cały wiersz opisu.
 */
const Kafelek = ({ etykieta, wartosc, opis }) => {
  if (wartosc === null || wartosc === undefined || wartosc === '') return null
  return (
    <div className="rounded-lg border px-3 py-2.5" style={{ borderColor: 'var(--vv-dok-obwodka)' }}>
      <span className="block w-5 h-0.5 rounded mb-1.5" style={{ backgroundColor: 'var(--vv-dok)' }} />
      <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-500 leading-tight">
        {etykieta}
      </p>
      <p className="mt-0.5 text-lg font-bold text-gray-900 tabular-nums leading-tight">{wartosc}</p>
      {opis && <p className="mt-0.5 text-[10px] text-gray-400 leading-snug">{opis}</p>}
    </div>
  )
}

const StronaTytulowa = ({
  nazwaFirmy, logoFirmy, kwp, data, adres,
  naGruncie = false, zMagazynem = false,
  produkcjaKwh = null, uzyskKwhNaKwp = null,
  pojemnoscMagazynuKwh = null,
  cenaZl = null, cenaNettoZl = null, stawkaVat = null,
  liczbaModulow = null, mocModuluW = null,
  spisTresci = [],
  numer = 1, zeStron = 5, podtytul = '',
  // Oferta w karcie klienta: dla kogo, numer i ważność, oraz ujęcie z Projektu 3D (hooks/useUjecie3D).
  klient = null, oferta = null, ujecie3d = null, ujecieTrwa = false,
}) => {
  const { t } = useTranslation('projekt')
  return (
  <StronaDokumentu
    numer={numer} zeStron={zeStron} nazwaFirmy={nazwaFirmy}
    podtytul={podtytul} pierwsza zNaglowkiem={false}
  >
    {/* Pas z logo i nazwą firmy. Logo po lewej, bo tam pada wzrok pierwszy raz;
        nazwa po prawej, żeby przy braku logo strona nie zaczynała się pustką. */}
    <div className="flex items-start justify-between gap-6 mb-6">
      <div className="min-w-0">
        {logoFirmy && (
          <img src={logoFirmy} alt={nazwaFirmy || t('modal.logo_firmy')}
               className="h-14 w-auto max-w-[220px] object-contain
                          print:h-20 print:max-w-[280px]" />
        )}
      </div>
      <div className="text-right min-w-0">
        {nazwaFirmy && (
          <p className="text-lg font-bold text-gray-900 leading-tight truncate">{nazwaFirmy}</p>
        )}
        <p className="text-xs text-gray-500 mt-0.5">{t('tytulowa.przygotowany', { data })}</p>
      </div>
    </div>

    {/* Pas tytułowy wychodzi POZA margines strony aż do krawędzi kartki —
        ujemny margines dokładnie znosi wcięcie z `StronaDokumentu`, więc nic
        nie wystaje poza ekran i nie pojawia się poziome przewijanie. */}
    <div
      className={`-mx-6 sm:-mx-8 px-6 sm:px-8 ${ujecie3d ? 'py-6 mb-0 print:py-5' : 'py-8 mb-7 print:py-7 print:mb-5'}
                 print:-mx-[12mm] print:px-[12mm]`}
      style={{ backgroundColor: 'var(--vv-dok)', color: 'var(--vv-dok-na-pasku)' }}
    >
      {/* Napisy dziedziczą kolor po pasie (`currentColor`), a nie mają wpisanej
          bieli: przy jasnym kolorze firmy — żółci czy pomarańczu — biały tekst
          byłby nieczytelny. Wybór robi `tekstNaTle` w lib/kolorDokumentu. */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-6 sm:gap-10 items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-[1.1]">
            {t('tytulowa.tytul_1')}<br />{t('tytulowa.tytul_2')}
          </h1>
          <span className="block w-16 h-1 rounded mt-4 mb-4 opacity-70"
                style={{ backgroundColor: 'currentColor' }} />
          <p className="text-sm leading-6 max-w-md opacity-80">
            {zMagazynem ? t('tytulowa.opis_z_magazynem') : t('tytulowa.opis')}
          </p>
        </div>

        <ul className="space-y-2.5">
          {OBIETNICE.map((klucz) => (
            <li key={klucz} className="flex items-start gap-2.5 opacity-90">
              <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-px" />
              <span className="text-sm leading-snug">{t(`tytulowa.obietnice.${klucz}`)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/*
      SZEROKOŚĆ NA WYDRUKU JEST ZARAZEM WYSOKOŚCIĄ — pliki w `public/projekt`
      są KWADRATOWE (640 × 640). Przy `88mm` grafika zjadała 88 mm wysokości
      i cała okładka wychodziła ok. 275 mm przy 273 mm dostępnych na A4
      (297 mm minus marginesy `@page`). Nadmiar spływał na arkusz drugi, dając
      stronę z kilkoma milimetrami treści i pustką pod spodem.

      62 mm zostawia ok. 25 mm zapasu na dłuższą nazwę firmy albo dodatkowy
      wiersz w spisie treści. Zmieniając tę wartość, pamiętaj, że rośnie
      W DWÓCH wymiarach naraz.
    */}
    {/* Ujęcie z Projektu 3D na pierwszym planie, od krawędzi do krawędzi pod pasem tytułowym. Wysokość na
        wydruku 92 mm: z niższym pasem i bez spisu treści okładka mieści się w 273 mm arkusza. */}
    {ujecie3d ? (
      <img src={ujecie3d} alt="Budynek z modułami fotowoltaicznymi, model 3D"
           className="-mx-6 sm:-mx-8 mb-6 block w-[calc(100%+3rem)] sm:w-[calc(100%+4rem)] max-w-none aspect-[16/9] object-cover
                      print:-mx-[12mm] print:w-[calc(100%+24mm)] print:h-[92mm] print:aspect-auto print:mb-5" />
    ) : ujecieTrwa ? (
      <div className="mx-auto mb-7 flex aspect-[16/9] w-full max-w-lg items-center justify-center rounded-xl bg-gray-100 text-sm text-gray-500 print:hidden">
        Przygotowuję ujęcie z Projektu 3D…
      </div>
    ) : null}
    {!ujecie3d && <img
      src={grafikaDla({ naGruncie, zMagazynem })}
      alt={t(`tytulowa.alt_${naGruncie ? 'grunt' : 'dach'}${zMagazynem ? '_magazyn' : ''}`)}
      className={`mx-auto mb-7 w-64 sm:w-80 h-auto print:w-[58mm] print:mb-4 ${ujecieTrwa ? 'hidden print:block' : ''}`}
    />}

    {(klient?.nazwa || oferta?.numer) && (
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-gray-200 pb-3 text-sm print:mb-4">
        {klient?.nazwa && (
          <p className="min-w-0 text-gray-900">
            <span className="text-gray-500">Dla: </span><span className="font-semibold">{klient.nazwa}</span>
            {klient.adres && <span className="text-gray-500">, {klient.adres}</span>}
          </p>
        )}
        {oferta?.numer && (
          <p className="tabular-nums text-gray-600">
            Oferta <span className="font-semibold text-gray-900">{oferta.numer}</span>
            {oferta.wazneDo && <> · ważna do <span className="font-semibold text-gray-900">{oferta.wazneDo}</span></>}
          </p>
        )}
      </div>
    )}

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-7 print:mb-5">
      <Kafelek
        etykieta={t('tytulowa.moc_instalacji')}
        wartosc={kwp ? `${String(kwp).replace('.', ',')} kWp` : null}
        opis={liczbaModulow && mocModuluW
          ? t('tytulowa.modulow_po', { liczba: liczbaModulow, moc: mocModuluW })
          : null}
      />
      {/* Zaokrąglenie do pełnych kilowatogodzin: prognoza wychodzi z modelu jako
          ułamek („8386,42 kWh"), a części dziesiętne w liczbie rzędu tysięcy
          udają dokładność, której ta prognoza nie ma. */}
      <Kafelek
        etykieta={t('tytulowa.produkcja_roczna')}
        wartosc={produkcjaKwh ? `${Math.round(produkcjaKwh).toLocaleString(KRAJ.locale)} kWh` : null}
        opis={uzyskKwhNaKwp ? t('tytulowa.z_1_kwp', { kwh: Math.round(uzyskKwhNaKwp) }) : null}
      />
      <Kafelek
        etykieta={t('tytulowa.magazyn_energii')}
        wartosc={pojemnoscMagazynuKwh ? `${String(pojemnoscMagazynuKwh).replace('.', ',')} kWh` : null}
      />
      <Kafelek
        etykieta={t('tytulowa.cena_z_montazem')}
        wartosc={cenaZl ? `${formatPrice(Math.round(cenaZl))} ${KRAJ.symbol}` : null}
        opis={cenaNettoZl != null
          ? t('tytulowa.netto', { kwota: `${formatPrice(cenaNettoZl)} ${KRAJ.symbol}` })
          : stawkaVat != null ? t('tytulowa.vat_w_stawce', { stawka: stawkaVat }) : null}
      />
    </div>

    {adres && !klient && <p className="mb-6 text-sm text-gray-600">{adres}</p>}

    {/* Z ujęciem 3D spis treści nie mieści się na okładce; dokument jest krótki i ma numery stron. */}
    {spisTresci.length > 0 && !ujecie3d && (
      <div>
        <p className="text-sm font-bold text-gray-900 mb-3">{t('tytulowa.spis_tresci')}</p>
        <ul className="space-y-2">
          {spisTresci.map(({ tytul, opis }, i) => (
            <li key={tytul} className="flex items-baseline gap-3 text-sm">
              <span className="font-bold tabular-nums w-6 flex-shrink-0"
                    style={{ color: 'var(--vv-dok-ciemny)' }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="font-semibold text-gray-900">{tytul}</span>
              <span className="text-gray-400 text-xs leading-snug">{opis}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </StronaDokumentu>
  )
}

export default StronaTytulowa
